import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TYPES = [['candidate', 'Careers candidate (job applicant)'], ['employee', 'HED employee'], ['client', 'Client user'], ['admin', 'Admin user'], ['other', 'Other / not sure']];

export default function DeleteAccountPage() {
  const [f, setF] = React.useState({ name: '', email: '', account_type: 'candidate', reason: '', confirm: false });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [done, setDone] = React.useState(null);
  React.useEffect(() => { document.title = 'Delete your account · Girgit'; }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { const r = await axios.post(`${API}/api/public/account-deletion`, f); setDone(r.data); }
    catch (err) { setError(err?.response?.data?.detail || 'Could not submit. Please email media@halfeatendonut.in'); }
    finally { setBusy(false); }
  };
  const input = 'mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300';
  return (
    <div className="min-h-screen bg-[#f6f6f4]" data-testid="delete-account-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_careers-hub-9/artifacts/6q64xjbl_HED%20NEW%20LOGO%20BLACK%20PNG.png" alt="Half Eaten Donut" className="h-7" />
          <span className="text-sm font-semibold text-slate-700 flex-1">Girgit</span>
          <nav className="flex gap-4 text-xs font-semibold">
            <Link to="/privacy" className="text-slate-500 hover:text-slate-900">Privacy</Link>
            <Link to="/terms" className="text-slate-500 hover:text-slate-900">Terms</Link>
          </nav>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-10 space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 text-[15px] leading-relaxed text-slate-700">
          <h1 className="text-3xl font-bold text-slate-900 mb-3">Delete your Girgit account</h1>
          <p className="mb-3">Use this form to ask Half Eaten Donut Private Limited to delete your Girgit account and the personal data linked to it. It works for the website and the Android and iOS apps.</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-3">
            <li><b>What we delete:</b> your account login, profile and contact details, uploaded files, job applications (for candidates) and device notification tokens.</li>
            <li><b>What we may keep:</b> records we must hold by law, such as invoices, tax, payroll and signed contract records (generally up to 8 years), plus a record of this request. Everything else is removed.</li>
            <li><b>How long it takes:</b> we confirm your identity by email and complete the request within 30 days.</li>
          </ul>
          <p>You can also email <a href="mailto:media@halfeatendonut.in?subject=Data%20deletion%20request" className="text-fuchsia-700 underline">media@halfeatendonut.in</a> with the subject &quot;Data deletion request&quot;. See our <Link to="/privacy" className="text-fuchsia-700 underline">Privacy Policy</Link> for details.</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10">
          {done ? (
            <div className="flex gap-3 items-start text-emerald-800" data-testid="delete-account-success">
              <CheckCircle2 className="w-6 h-6 shrink-0" />
              <div><p className="font-semibold">{done.message}</p><p className="text-sm mt-1">Reference: <b data-testid="delete-account-reference">{done.reference}</b>. We&apos;ll email you at the address you gave.</p></div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="delete-account-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block text-xs font-semibold text-slate-600">Full name *<input className={input} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="delete-account-name" /></label>
                <label className="block text-xs font-semibold text-slate-600">Email linked to your account *<input type="email" className={input} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="delete-account-email" /></label>
              </div>
              <label className="block text-xs font-semibold text-slate-600">Account type *
                <select className={`${input} bg-white`} value={f.account_type} onChange={(e) => setF({ ...f, account_type: e.target.value })} data-testid="delete-account-type">
                  {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="block text-xs font-semibold text-slate-600">Anything we should know? (optional)
                <textarea rows={3} className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="delete-account-reason" />
              </label>
              <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.checked })} className="mt-0.5 w-4 h-4 accent-fuchsia-600" data-testid="delete-account-confirm" />
                I want my Girgit account and personal data deleted. I understand this cannot be undone.
              </label>
              {error && <p className="text-sm text-rose-600" data-testid="delete-account-error">{error}</p>}
              <button type="submit" disabled={busy || !f.confirm} data-testid="delete-account-submit"
                className="inline-flex items-center gap-2 px-6 h-11 rounded-full bg-rose-600 text-white text-sm font-semibold disabled:opacity-40 hover:bg-rose-700 transition-colors">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Request deletion
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
